import React from 'react';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

export const parseDateInput = (dateInput) => {
  if (!dateInput) return null;

  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : new Date(dateInput);
  }

  const value = String(dateInput).trim();

  const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    const parsed = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dmyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch;
    const parsed = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toStartOfDay = (dateInput) => {
  const parsed = parseDateInput(dateInput);
  if (!parsed) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

// Utility functions for consistent formatting across the app
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '-';
  const date = parseDateInput(dateString);
  if (!date) return 'Invalid Date';

  const defaultOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return date.toLocaleDateString('en-GB', { ...defaultOptions, ...options });
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const number = Number(value);
  return Number.isNaN(number) ? 'N/A' : `£${number.toFixed(2)}`;
};

export const formatRelativeTime = (dateString) => {
  if (!dateString) return { text: '-', color: 'bg-gray-100 text-gray-400' };
  const today = toStartOfDay(new Date());
  const due = toStartOfDay(dateString);
  if (!today || !due) return { text: '-', color: 'bg-gray-100 text-gray-400' };

  const diffTime = due - today;
  const days = Math.round(diffTime / DAY_IN_MS);
  const absDays = Math.abs(days);

  if (days < 0) return { text: `${absDays} ${absDays === 1 ? 'day' : 'days'} ago`, color: 'bg-red-600 text-white' };
  if (days === 0) return { text: 'Due Today', color: 'bg-amber-600 text-white' };
  if (days === 1) return { text: 'Due Tomorrow', color: 'bg-amber-600 text-white' };
  if (days <= 60) return { text: `in ${days} days`, color: days <= 14 ? 'bg-amber-600 text-white' : 'bg-emerald-600 text-white' };

  const months = Math.ceil(days / 30);
  return { text: `in ${months} ${months === 1 ? 'month' : 'months'}`, color: 'bg-emerald-600 text-white' };
};

// Loading skeleton component
export const LoadingSkeleton = ({ className = "", lines = 1 }) => (
  <div className={`animate-pulse ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-4 bg-slate-200 rounded mb-2 last:mb-0"></div>
    ))}
  </div>
);

// Card component with consistent styling
export const Card = ({ children, className = "", onClick, hover = true }) => (
  <div
    className={`bg-white rounded-lg border border-slate-200 shadow-sm transition-all duration-200 ${
      hover ? 'hover:shadow-md hover:border-slate-300' : ''
    } ${onClick ? 'cursor-pointer' : ''} ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// Button component with consistent styling
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 focus:ring-slate-500',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    warning: 'bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};

// Badge component for status indicators
export const Badge = ({ children, variant = 'default', size = 'sm', className = '' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700 border-slate-200',
    success: 'bg-emerald-600 text-white border-transparent',
    warning: 'bg-amber-600 text-white border-transparent',
    danger: 'bg-red-600 text-white border-transparent',
    info: 'bg-blue-600 text-white border-transparent'
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  return (
    <span className={`inline-flex items-center font-semibold border rounded-full ${variants[variant]} ${sizes[size]} ${className}`}>
      {children}
    </span>
  );
};

// Input component with consistent styling
export const Input = ({
  label,
  error,
  required = false,
  className = '',
  containerClassName = '',
  ...props
}) => (
  <div className={`space-y-1 ${containerClassName}`}>
    {label && (
      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <input
      className={`w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white text-slate-800 transition-colors ${error ? 'border-red-300 focus:ring-red-500' : ''} ${className}`}
      {...props}
    />
    {error && <p className="text-xs text-red-600">{error}</p>}
  </div>
);

// Select component with consistent styling
export const Select = ({
  label,
  options = [],
  placeholder = 'Select...',
  error,
  required = false,
  className = '',
  containerClassName = '',
  ...props
}) => (
  <div className={`space-y-1 ${containerClassName}`}>
    {label && (
      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <select
      className={`w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white text-slate-800 transition-colors appearance-none cursor-pointer ${error ? 'border-red-300 focus:ring-red-500' : ''} ${className}`}
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && <p className="text-xs text-red-600">{error}</p>}
  </div>
);